import { Fragment, useMemo, useState } from 'react';
import { Alert } from '../../../components/ui/Alert';
import { Button } from '../../../components/ui/Button';
import { Card, StatTile } from '../../../components/ui/Card';
import { Modal } from '../../../components/ui/Modal';
import { EmptyState, ErrorState } from '../../../components/ui/States';
import { Nil, Table, TableFooter, TGroupRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { useBuckets, useCreateBucket, useReps, useSetRepRole } from '../../../hooks/usePilots';
import type { BucketStatus, BucketSummary, RepSetting } from '../../../api/pilots';
import { plural } from '../../ProjectManagement/formatters';
import { formatDate } from '../outreach/format';
import { BUCKET_STATUS_META, pct, repName } from './format';
import { NextStep, TableSkeleton, selectClass } from './parts';
import { SectionToolbar } from '../SectionToolbar';

/** "14:05", the time the buckets were read (fixed 24-hour format, no locale month names). */
function formatTime(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

const inputClass =
  'w-full rounded-md border border-border-light bg-surface-primary px-3 py-2 text-body-sm text-text-primary placeholder-text-muted focus:outline-none focus:ring-1 focus:ring-accent';

function NewBucketModal({ isOpen, onClose, onCreated }: { isOpen: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('');
  const create = useCreateBucket();
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New bucket" description="One signal we go after, e.g. “Bid but lost a big tender (+€2M)”. The launch checklist is added for you." size="md">
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate(name.trim(), { onSuccess: ({ id }) => { setName(''); onCreated(id); } });
        }}
      >
        <input autoFocus className={inputClass} placeholder="Bucket name" value={name} onChange={(e) => setName(e.target.value)} />
        {create.error && <Alert variant="error">Could not create the bucket: {create.error.message}</Alert>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" isLoading={create.isPending} disabled={!name.trim()}>Create</Button>
        </div>
      </form>
    </Modal>
  );
}

const STATUS_ORDER: BucketStatus[] = ['active', 'planned', 'done'];
// The columns every load draws. Replied is left out: it only appears once someone has replied,
// so a skeleton showing it would lose a column when the data lands.
const SKELETON_COLUMNS = [
  { label: 'Bucket' },
  { label: 'Next step' },
  { label: 'People', align: 'right' },
  { label: 'Reached', align: 'right' },
  { label: 'Meetings', align: 'right' },
] as const;

/** The same four tiles as TotalsTiles, valued "—" (not yet known, never 0), so the page keeps its shape while it loads. */
const TILE_LABELS = ['People', 'Reached', 'Meetings', 'Active buckets blocked'] as const;
function TotalsTilesSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3 animate-pulse lg:grid-cols-4" aria-hidden>
      {TILE_LABELS.map((label) => (
        <StatTile key={label} label={label} value="—" sub="loading" />
      ))}
    </div>
  );
}

/** Meetings are counted since launch, so a bucket that has not launched has none to show. */
const isLaunched = (b: BucketSummary) => b.status !== 'planned' || b.startedAt !== null;

/** "12 companies · launched 3 Sep" — the grey line under a bucket's name. */
function bucketSubline(bucket: BucketSummary): string {
  const parts = [plural(bucket.companies, 'company', 'companies')];
  if (bucket.startedAt) parts.push(`launched ${formatDate(bucket.startedAt)}`);
  else parts.push(`checklist ${bucket.checklist.done}/${bucket.checklist.total}`);
  return parts.join(' · ');
}

/** A count with its share of the bucket's people as a grey sub-line. */
function Share({ n, of }: { n: number; of: number }) {
  return (
    <span className="tabular-nums whitespace-nowrap">
      <span className={n === 0 ? 'block text-text-tertiary' : 'block text-text-primary'}>{n}</span>
      {of > 0 && n > 0 && <span className="block text-body-xs text-text-tertiary">{pct(n, of)}</span>}
    </span>
  );
}

function BucketRow({ bucket, showReplied, onOpen }: { bucket: BucketSummary; showReplied: boolean; onOpen: () => void }) {
  const f = bucket.funnel;
  const launched = isLaunched(bucket);
  return (
    <Tr onClick={onOpen}>
      <Td>
        <p className="font-medium text-text-primary">{bucket.name}</p>
        <p className="text-body-xs text-text-tertiary">{bucketSubline(bucket)}</p>
      </Td>
      <Td>
        {/* No people is the next step, not "nothing blocking". A healthy bucket shows nothing here. */}
        {f.people === 0 ? (
          <span className="text-body-xs text-text-tertiary">No people yet: import a list</span>
        ) : (
          <NextStep blocking={bucket.blocking} quiet={bucket.status !== 'active'} />
        )}
      </Td>
      {/* Unassigned people are already the "N people without a rep" next step, so no second count here. */}
      {f.people === 0 ? (
        // Nobody imported yet: muted zeros (the Next step already says "import a list"), never a row of
        // "·" that reads as missing data. A meeting can still exist (matched on the company's domain or
        // ticked by hand), so that column keeps its own value.
        <>
          <Td align="right" className="tabular-nums text-text-tertiary">0</Td>
          <Td align="right" className="tabular-nums text-text-tertiary">{launched ? 0 : <Nil />}</Td>
          {showReplied && <Td align="right" className="tabular-nums text-text-tertiary">{launched ? 0 : <Nil />}</Td>}
        </>
      ) : (
        <>
          <Td align="right" className="tabular-nums text-text-primary">{f.people}</Td>
          {/* Reached and Replied count touches since launch, like Meetings: a bucket that has not launched has none. */}
          <Td align="right">
            {launched ? <Share n={f.reached} of={f.people} /> : <span title="Counted from the day a bucket launches"><Nil /></span>}
          </Td>
          {showReplied && (
            <Td align="right">
              {launched ? <Share n={f.discussions} of={f.reached} /> : <span title="Counted from the day a bucket launches"><Nil /></span>}
            </Td>
          )}
        </>
      )}
      <Td align="right">
        {!isLaunched(bucket) ? (
          <span title="Meetings are counted from the day a bucket launches"><Nil /></span>
        ) : f.people === 0 && f.meetings > 0 ? (
          // No people to take a share of: say where the meeting came from instead of a bare count.
          <span className="tabular-nums whitespace-nowrap" title="Matched on the bucket's company domains or ticked by hand; nobody has been imported into the bucket">
            <span className="block text-text-primary">{f.meetings}</span>
            <span className="block text-body-xs text-text-tertiary">by company, no people</span>
          </span>
        ) : (
          <Share n={f.meetings} of={f.people} />
        )}
      </Td>
    </Tr>
  );
}

function groupSummary(buckets: BucketSummary[]): string {
  const sum = (pick: (b: BucketSummary) => number) => buckets.reduce((n, b) => n + pick(b), 0);
  return [
    plural(buckets.length, 'bucket'),
    plural(sum((b) => b.funnel.people), 'person', 'people'),
    `${buckets.filter(isLaunched).reduce((n, b) => n + b.funnel.reached, 0)} reached`,
    plural(buckets.filter(isLaunched).reduce((n, b) => n + b.funnel.meetings, 0), 'meeting'),
  ].join(' · ');
}

/** Answer first: across every bucket, how many people, how many reached, how many meetings, how many buckets blocked. */
function TotalsTiles({ buckets }: { buckets: BucketSummary[] }) {
  const sum = (pick: (b: BucketSummary) => number) => buckets.reduce((n, b) => n + pick(b), 0);
  const people = sum((b) => b.funnel.people);
  const launched = buckets.filter(isLaunched);
  const reached = launched.reduce((n, b) => n + b.funnel.reached, 0);
  const launchedPeople = launched.reduce((n, b) => n + b.funnel.people, 0);
  const meetings = launched.reduce((n, b) => n + b.funnel.meetings, 0);
  // Red-worthy only for a bucket that has launched: a planned bucket's open checklist is the plan, not a fault.
  const active = buckets.filter((b) => b.status === 'active');
  const planned = buckets.filter((b) => b.status === 'planned').length;
  const blocked = active.filter((b) => b.blocking.length > 0 || b.funnel.people === 0);
  const empty = blocked.filter((b) => b.funnel.people === 0).length;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile label="People" value={people.toLocaleString('en-GB')} sub={`in ${plural(buckets.length, 'bucket')} · imported lists`} />
      <StatTile
        label="Reached"
        value={reached.toLocaleString('en-GB')}
        sub={
          launched.length === 0
            ? 'no bucket has launched yet'
            : `${launchedPeople > 0 ? `${pct(reached, launchedPeople)} of people · ` : ''}1+ real touch in lemlist, ${plural(launched.length, 'launched bucket')}`
        }
      />
      <StatTile label="Meetings" value={meetings} sub={
          launched.length === 0
            ? 'no bucket has launched yet'
            : `since launch, ${plural(launched.length, 'launched bucket')} · Calendly, Attio, ticked by hand`
        }
      />
      <StatTile
        label="Active buckets blocked"
        value={blocked.length}
        sub={
          blocked.length > 0
            ? [empty > 0 ? `${empty} with no people` : null, blocked.length - empty > 0 ? `${blocked.length - empty} waiting on a step` : null]
                .filter(Boolean)
                .join(' · ')
            : `of ${active.length} active${planned > 0 ? ` · ${planned} planned, not launched` : ''}`
        }
      />
    </div>
  );
}

/** SDR or AE per rep. Set once, so it sits behind a button. SDRs write English only; the bucket pages flag anyone given to the wrong one. */
function RepRolesModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const reps = useReps();
  const set = useSetRepRole();
  const list = reps.data?.reps ?? [];
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Team roles" description="SDRs write English only. AEs also write Dutch, French and Spanish. Not a rep: left out of every rep picker." size="md">
      <div className="space-y-2">
        {reps.error && <Alert variant="error">Could not load the reps from lemlist: {reps.error.message}</Alert>}
        {list.map((r) => (
          <label key={r.email} className="flex items-center justify-between gap-3 text-body-sm text-text-primary">
            {repName(r)}
            <select
              className={`${selectClass} w-28 ${r.role || r.off ? '' : 'border-warning text-warning-dark'}`}
              value={r.off ? 'off' : r.role ?? ''}
              disabled={set.isPending}
              onChange={(e) => set.mutate({ email: r.email, role: (e.target.value || null) as RepSetting | null })}
            >
              <option value="">not set</option>
              <option value="sdr">SDR</option>
              <option value="ae">AE</option>
              <option value="off">Not a rep</option>
            </select>
          </label>
        ))}
        {set.error && <Alert variant="error">Not saved: {set.error.message}</Alert>}
      </div>
    </Modal>
  );
}

/** Sales → Outreach Buckets: every bucket, active first, then planned, then done. One line each: where it stands and what blocks it. */
export function BucketsList({ isAdmin, onOpen }: { isAdmin: boolean; onOpen: (id: string) => void }) {
  const query = useBuckets();
  const [creating, setCreating] = useState(false);
  const [roles, setRoles] = useState(false);
  const buckets = useMemo(() => query.data?.buckets ?? [], [query.data]);
  // A Replied column that is 0 on every bucket says nothing: it is drawn once someone has replied.
  const showReplied = buckets.some((b) => b.funnel.discussions > 0);
  const colCount = showReplied ? 6 : 5;
  const groups = useMemo(
    () =>
      STATUS_ORDER.map((status) => ({ status, buckets: buckets.filter((b) => b.status === status) })).filter(
        (g) => g.buckets.length > 0
      ),
    [buckets]
  );

  return (
    <div className="space-y-4">
      <SectionToolbar
        meta={
          <span>
            From the imported lists, lemlist activity and Calendly / Attio calendars
            {query.dataUpdatedAt > 0 && ` · loaded ${formatTime(query.dataUpdatedAt)}`}
          </span>
        }
        info={
          <>
            <span className="font-medium">People</span>: everyone imported into a bucket.{' '}
            <span className="font-medium">Reached</span>: at least one real touch in lemlist (hourly lemlist sync).{' '}
            <span className="font-medium">Meetings</span>: since the bucket launched, from Calendly bookings and Attio
            calendars matched on the person or the company domain, plus meetings ticked by hand. All counts are live,
            not for a period.
          </>
        }
        actions={
          isAdmin ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setRoles(true)}>Team roles</Button>
              <Button size="sm" onClick={() => setCreating(true)}>New bucket</Button>
            </>
          ) : undefined
        }
      />

      {query.isLoading ? (
        <>
          <TotalsTilesSkeleton />
          <Card padding="md"><TableSkeleton columns={[...SKELETON_COLUMNS]} rows={4} minWidth={760} /></Card>
        </>
      ) : query.error ? (
        <ErrorState title="Couldn’t load the buckets" error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
        {buckets.length > 0 && <TotalsTiles buckets={buckets} />}
        <Card padding="md">
          {buckets.length === 0 ? (
            <EmptyState title="No buckets yet" description={isAdmin ? 'Start one with “New bucket”.' : undefined} compact />
          ) : (
            <>
              <Table minWidth={760}>
                <THead>
                  <tr>
                    <Th>Bucket</Th>
                    <Th>Next step</Th>
                    <Th align="right" title="Everyone in the bucket">People</Th>
                    <Th align="right" title="At least one real touch in lemlist · % of people">Reached</Th>
                    {showReplied && <Th align="right" title="Replied at least once · % of people reached">Replied</Th>}
                    <Th align="right" title="Meetings since launch · % of people">Meetings</Th>
                  </tr>
                </THead>
                <tbody>
                  {groups.map((g) => (
                    <Fragment key={g.status}>
                      {/* One group: its header would repeat the footer word for word. */}
                      {groups.length > 1 && (
                        <TGroupRow colSpan={colCount} label={BUCKET_STATUS_META[g.status].label} summary={groupSummary(g.buckets)} />
                      )}
                      {g.buckets.map((b) => <BucketRow key={b.id} bucket={b} showReplied={showReplied} onOpen={() => onOpen(b.id)} />)}
                    </Fragment>
                  ))}
                </tbody>
              </Table>
              <TableFooter>
                {groups.length === 1 && `${BUCKET_STATUS_META[groups[0]!.status].label} · `}
                {groupSummary(buckets)}
              </TableFooter>
            </>
          )}
        </Card>
        </>
      )}

      {isAdmin && roles && <RepRolesModal isOpen={roles} onClose={() => setRoles(false)} />}
      <NewBucketModal isOpen={creating} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); onOpen(id); }} />
    </div>
  );
}
